import { useState, useEffect, useCallback } from 'react';
import './FeatureShowcase.css';

const features = [
  {
    id: 1,
    title: 'Group Overview Dashboard',
    description: 'Get a comprehensive view of your group\'s financial health with real-time statistics on members, payments, and total collections.',
    icon: '📊',
    highlights: ['Total Members', 'Active Members', 'Total Collected', 'Total Payments']
  },
  {
    id: 2,
    title: 'Member Management',
    description: 'Easily manage all group members with detailed profiles, payment history, status tracking, and bulk import/export capabilities.',
    icon: '👥',
    highlights: ['Member Profiles', 'Payment History', 'Status Tracking', 'CSV/Excel Export']
  },
  {
    id: 3,
    title: 'Payment Tracking',
    description: 'Track all payments with case numbers, filter by date ranges, and export detailed reports in multiple formats.',
    icon: '💳',
    highlights: ['Case Number Tracking', 'Date Filtering', 'Import History', 'Multi-format Export']
  },
  {
    id: 4,
    title: 'Smart Notifications',
    description: 'Send automated payment reminders with customizable deadlines, penalty actions, and probation periods.',
    icon: '🔔',
    highlights: ['Payment Reminders', 'Custom Messages', 'Deadline Enforcement', 'Penalty Options']
  },
  {
    id: 5,
    title: 'Secure Disbursements',
    description: 'Process bank transfers or manual disbursements with multi-signature approval for maximum security.',
    icon: '💰',
    highlights: ['Bank Transfers', 'Manual Options', 'Proof Upload', '3-Signatory Approval']
  }
];

export default function FeatureShowcase() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % features.length);
    setProgress(0);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + features.length) % features.length);
    setProgress(0);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlaying) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          nextSlide();
          return 0;
        }
        return prev + 2; // 2% every 100ms = 5 seconds total
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [isAutoPlaying, nextSlide]);

  const currentFeature = features[currentSlide];

  return (
    <section className="feature-showcase" data-testid="feature-showcase">
      <div className="showcase-header">
        <h2>See ChamaLink in Action</h2>
        <p>Discover how our platform simplifies group financial management</p>
      </div>

      <div 
        className="showcase-container"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        {/* Progress Bar */}
        <div className="progress-bar-container">
          <div 
            className="progress-bar" 
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Main Slide Content */}
        <div className="slide-content">
          <div className="slide-visual">
            <div className="feature-mockup">
              <div className="mockup-header">
                <div className="mockup-dots">
                  <span className="dot red"></span>
                  <span className="dot yellow"></span>
                  <span className="dot green"></span>
                </div>
                <span className="mockup-title">ChamaLink Dashboard</span>
              </div>
              <div className="mockup-body">
                <div className="mockup-sidebar">
                  <div className="sidebar-item active">📊 Overview</div>
                  <div className="sidebar-item">👥 Members</div>
                  <div className="sidebar-item">💳 Payments</div>
                  <div className="sidebar-item">🔔 Notifications</div>
                  <div className="sidebar-item">💰 Disbursements</div>
                </div>
                <div className="mockup-main">
                  <div className="feature-icon-large">{currentFeature.icon}</div>
                  <h3 className="feature-title-display">{currentFeature.title}</h3>
                  <div className="feature-highlights-grid">
                    {currentFeature.highlights.map((highlight, idx) => (
                      <div key={idx} className="highlight-chip">
                        <span className="highlight-check">✓</span>
                        {highlight}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="slide-info">
            <div className="slide-number">
              <span className="current">{String(currentSlide + 1).padStart(2, '0')}</span>
              <span className="divider">/</span>
              <span className="total">{String(features.length).padStart(2, '0')}</span>
            </div>
            <h3>{currentFeature.title}</h3>
            <p>{currentFeature.description}</p>
            
            <div className="feature-highlights">
              {currentFeature.highlights.map((highlight, idx) => (
                <span key={idx} className="highlight-tag">
                  {highlight}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="slide-navigation">
          <button 
            className="nav-btn prev" 
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            ←
          </button>
          
          <div className="slide-dots">
            {features.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
          
          <button 
            className="nav-btn next" 
            onClick={nextSlide}
            aria-label="Next slide"
          >
            →
          </button>
        </div>

        {/* Auto-play indicator */}
        <div className="autoplay-indicator">
          {isAutoPlaying ? '▶ Auto-playing' : '⏸ Paused'}
        </div>
      </div>
    </section>
  );
}
